import { IS_APP } from 'settings';
import NewCourseShelf from './CoursesShelf';
import { useCallback, useEffect } from 'react';
import OffersShelf from './OffersShelf';
import Typography from '@material-ui/core/Typography';
import SchoolsShelf from './SchoolsShelf';
import { IShelf } from 'interface/showcase';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import { connect, useDispatch } from 'react-redux';
import SkeletonList from 'components/Shared/SkeletonList';
import Skeleton from '@material-ui/lab/Skeleton';
import nothingToShowImage from 'assets/img/nocomments.png';
import KeepWatching from './KeepWatching';
import { fetchShelves } from 'redux/showcase/slice';
import { useAppSelector } from 'hooks/redux';
import { toastSuccess } from 'utils/reduxToast';
import NutrorCast from './NutrorCast';
import InviteShelf from './Invites';
import * as inviteService from 'services/invites';
import { setInviteList } from 'redux/showcaseInvite/slice';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingBottom: theme.spacing(6),
    overflowX: 'hidden',
    minHeight: '100vh',
  },
  imgWrapper: {
    width: 400,
    height: 350,
    marginTop: theme.spacing(10),
    position: 'relative',
    '@media (max-width: 640px)': {
      transform: 'scale(0.7)',
      marginBottom: theme.spacing(-5),
      marginTop: theme.spacing(-5),
    },
  },
  waitingContainer: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(20),
    marginBottom: theme.spacing(20),
    padding: theme.spacing(0, 6),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(10),
      marginBottom: theme.spacing(10),
    },
    '& h1': {
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        fontSize: 20,
      },
    },
    '& p': {
      [theme.breakpoints.down('sm')]: {
        fontSize: 10,
      },
    },
    '& p, h1': {
      color: theme.palette.common.white,
    },
  },
  skeletonTitle: {
    height: 20,
    width: 140,
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
}));

const Shelves = () => {
  const dispatch = useDispatch();
  const shelves = useAppSelector((state) => state.showcase.shelves);
  const loading = useAppSelector((state) => state.showcase.loading);
  const update = useAppSelector((state) => state.showcase.update);
  const invites = useAppSelector((state) => state.showcaseInvite.list);
  const classes = useStyles();

  const fetchInviteList = useCallback(() => {
    inviteService.fetchInvites().then((list) => dispatch(setInviteList(list)));
  }, []);

  const shelfComponentMapper = (shelf: IShelf, index: number) => {
    const mapper = {
      category: <NewCourseShelf key={index} title={shelf.title} categoryId={shelf.id} />,
      shelf: <NewCourseShelf key={index} title={shelf.title} shelfId={shelf.id} />,
      school: <SchoolsShelf key={index} title={shelf.title} />,
      offer: <OffersShelf key={index} shelf={shelf} />,
      keepWatching: <KeepWatching key={index} title={shelf.title} />,
    };
    return mapper[shelf.type];
  };

  useEffect(() => {
    fetchInviteList();
  }, [fetchInviteList]);

  useEffect(() => {
    if (shelves == null) {
      dispatch(fetchShelves());
    }
  }, [dispatch, shelves]);

  useEffect(() => {
    if (update) {
      toastSuccess('Estamos atualizando a sua vitrine.');
      dispatch(fetchShelves());
    }
  }, [dispatch, update]);

  const showWaitingMessage = !(shelves ?? []).length && !loading && invites.length === 0;
  if (showWaitingMessage) {
    return (
      <div className={classes.waitingContainer}>
        <div className={classes.imgWrapper}>
          <img src={nothingToShowImage} alt='Vitrine vazia' />
        </div>
        <Typography variant='h1'>Seus cursos aparecerão aqui.</Typography>
        <Typography variant='body1'>Você poderá ver e acessar todos os seus cursos nessa página.</Typography>
      </div>
    );
  }
  const showSkeleton = !(shelves ?? []).length && loading;
  if (showSkeleton) {
    return (
      <div className={classes.container}>
        <Box padding={6}>
          <Skeleton variant='rect' className={classes.skeletonTitle} />
          <SkeletonList />
        </Box>
        <Box padding={6} paddingTop={0}>
          <Skeleton variant='rect' className={classes.skeletonTitle} />
          <SkeletonList />
        </Box>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {<InviteShelf />}
      {(shelves ?? []).map(shelfComponentMapper)}
      {IS_APP && <NutrorCast />}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.user,
});

export default connect(mapStateToProps)(Shelves);
